import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Pagination,
  Tooltip,
  PaginationItem,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useAppContext } from '../../../hooks/context/AppContext';
import alertIcon from "../../../assets/images/alert.png";
import bulbIcon from "../../../assets/images/bulb-light-icon.svg";
import "../../pages/styles/home.css";
import { useProjectApiClient } from "../../Services/api/projectService";
import { useParams } from "react-router-dom";
import { GridDeleteIcon } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Link,
} from "@mui/material";
import { successToast } from "v2/utils/toast";

interface UploadListProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshTrigger: number;
  moduleId: any;
}

const UploadList: React.FC<UploadListProps> = ({
  setLoading,
  refreshTrigger,
  moduleId,
}) => {
  const { appData, updateAppData, userData } = useAppContext();

  const { projectKey } = useParams<{ projectKey: string }>();
  const { getProjectFiles, deleteProjectFile } = useProjectApiClient();
  const [fileList, setFileList] = useState<any[]>([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [page, setPage] = useState(0); // zero-based for API
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProjects, setTotalProjects] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [fileId, setFileId] = useState(null);

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const moduleParam = searchParams.get("moduleId");
    let module_id = moduleId ? moduleId : moduleParam;
    if (projectKey) {
      fetchFiles(projectKey, module_id, page, rowsPerPage);
    }
  }, [projectKey, page, rowsPerPage, refreshTrigger]);


  useEffect(() => {
    if (userData) {
      setIsDisabled(!userData.permissions.CanEdit)
    }
  }, [userData]);

  const fetchFiles = async (
    key: string,
    moduleId: number,
    page: number,
    limit: number
  ) => {
    try {
      setLoading(true);
      const data = await getProjectFiles(key, moduleId, page + 1, limit); // API expects 1-based page
      setFileList(data.data);
      setTotalProjects(data.totalPages); // use totalPages directly for Pagination count
      setPage(data.currentPage - 1); // convert API 1-based page to 0-based
    } catch (error) {
      console.error("Failed to fetch files:", error);
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = (isoDate: string) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  let handleDelete = (val: any) => {
    setFileId(val.Id);
    setIsDeleteDialogOpen(true);
  };

  let handleDeleteProject = async () => {
    setLoading(true);
    setIsDeleteDialogOpen(false);
    let res = await deleteProjectFile(fileId);
    if (projectKey) {
      const searchParams = new URLSearchParams(location.search);
      const moduleParam = searchParams.get("moduleId");
      let module_id = moduleId ? moduleId : moduleParam;
      fetchFiles(projectKey, module_id, page, rowsPerPage);
      if (res) {
        successToast(res.message);
      }
    }
    setLoading(false);
    setFileId(null);
  };

  return (
    <>
      <ToastContainer />
      <div className="loadcontent-info-area">
        <div className="table-boxcard-project">
          <div className="table-project-archive">
            {fileList.length ? (
              <div className="mt-5" style={{ marginTop: "2%" }}>
                <table className="table project-archive-table">
                  <thead className="thead-project-arch">
                    <tr>
                      <th>Document Name</th>
                      <th>Uploaded on</th>
                      <th style={{ paddingLeft: "34px"}}>Actions </th>{" "}
                    </tr>
                  </thead>
                  <tbody className="tbody-project-arch">
                    {fileList.map((val: any, id: any) => (
                      <tr key={id}>
                        <td>{val.FileName}</td>
                        <td>{formattedDate(val.UploadDate)}</td>
                        <td>
                          <div className="download-button">
                            <Tooltip title="Download" placement="top" arrow>
                              <Chip
                                disabled={isDisabled}
                                onClick={() =>
                                  window.open(val.FileUrl, "_blank")
                                }
                                sx={{
                                  backgroundColor: "#fff", // lighter red
                                  color: "rgba(46, 25, 240, 1)", // white text
                                  cursor: "pointer",
                                  paddingX: 1,
                                  minWidth: 60,
                                  height: 28,
                                  fontSize: 12,
                                  "& .MuiChip-label": {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.2,
                                  },
                                  "&:hover": {
                                    // backgroundColor: "#388e3c", // slightly darker on hover
                                    color: "rgba(46, 25, 240, 1)",
                                  },
                                }}
                                label={
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.3}
                                  >
                                    <DownloadIcon
                                      sx={{
                                        fontSize: 14,
                                        color: "rgba(46, 25, 240, 1)",
                                      }}
                                    />
                                    Download
                                  </Box>
                                }
                              />
                            </Tooltip>

                            {localStorage?.user_info &&
                              JSON.parse(localStorage?.user_info).Role ==
                              "Admin" ? (
                              <Tooltip title="Delete" placement="top" arrow>
                                <Chip
                                  onClick={() => handleDelete(val)}
                                  sx={{
                                    backgroundColor: "#fff", // lighter red
                                    color: "#e53935", // white text
                                    cursor: "pointer",
                                    paddingX: 1,
                                    minWidth: 60,
                                    height: 28,
                                    fontSize: 12,
                                    "& .MuiChip-label": {
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.2,
                                    },
                                    "&:hover": {
                                      // backgroundColor: "#e53935", // slightly darker on hover
                                      color: "#e53935",
                                    },
                                  }}
                                  label={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.3}
                                    >
                                      <GridDeleteIcon
                                        sx={{ fontSize: 14, color: "#e53935" }}
                                      />
                                      Delete
                                    </Box>
                                  }
                                />
                              </Tooltip>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="record-not-available">
                <p>No Document Available</p>
              </div>
            )}
          </div>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
              width: "60%",
              gap: "20px",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "blue", fontWeight: 600 }}>
              {/* Total files
                            <Chip label={5} color="primary" sx={{ marginLeft: 1 }} /> */}
            </Typography>
            {fileList.length ? (
              <Pagination
                count={totalProjects}
                page={page + 1}
                onChange={(_, newPage) => setPage(newPage - 1)}
                siblingCount={1}
                boundaryCount={1}
                showFirstButton
                showLastButton
                renderItem={(item) => <PaginationItem {...item} />}
              />
            ) : null}
          </Box>
        </div>
      </div>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>
          <h3 className="modal-title">Confirm Delete Permanently</h3>
        </DialogTitle>
        <hr></hr>
        <DialogContent>
          <div className="save-card-wapper">
            <div className="alert-icon-wp">
              <img src={alertIcon} alt="" />
            </div>
            <h2 className="title-save-frm"></h2>
            <div className="txt-flex--mt">
              <span className="bulb-txt">
                <img src={bulbIcon} alt="" />
              </span>
              <p>
                {" "}
                Once deleted, this item can't be restored. Are you sure you want
                to delete it?
              </p>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="btn-cancel"
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProject}
            className="btn-delete"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadList;
